import { Alert, Button, Group, Text } from "@mantine/core";
import { IconUserExclamation } from "@tabler/icons-react";
import { useAuth, useClearAssumedRole } from "../hooks/useAuth";
import { roleDisplayName } from "../lib/roleDisplay";

/**
 * Banner that displays when user is viewing as a different role
 * Shown at the top of the application with option to revert
 *
 * Genuinely shared (frontend split epic Phase 7, Task 7.1 real-caller
 * check): assume-role is a test/non-prod-environment feature behind a
 * feature flag, and both apps/portal's AppLayout and apps/admin's
 * AdminLayout legitimately show this banner — not admin-only despite
 * AssumedRoleContext's own earlier "admin-only" note (that note was about
 * the context primitive being admin-only, corrected in Task 2.2 when
 * useAuth needed it generically; this banner was always the genuinely
 * shared piece).
 */
export function AssumedRoleBanner() {
  const { data: context } = useAuth();
  const clearRole = useClearAssumedRole();

  // Only show if user is currently assuming a role
  if (!context?.isAssuming || !context.assumedRole) {
    return null;
  }

  const displayName = roleDisplayName(context.assumedRole);

  return (
    <Alert
      icon={<IconUserExclamation size={20} />}
      title="Viewing as Different Role (read-only)"
      color="yellow"
      withCloseButton={false}
      styles={{
        // Positioning (sticky/top/z-index) lives on the wrapper Box in
        // AppLayout.tsx/AdminLayout.tsx, not here — this used to be
        // `top: 0` directly on the Alert, which sticks at the literal
        // viewport top (the same spot AppShell's fixed header occupies),
        // covering the header on scroll instead of sitting below it.
        root: {
          borderRadius: 0,
          borderLeft: "none",
          borderRight: "none",
          borderTop: "none",
        },
      }}
    >
      <Group justify="space-between">
        <Text size="sm">
          You are currently viewing the application as{" "}
          <Text component="span" fw={700}>
            {displayName}
          </Text>
          . This is a read-only preview — changes are disabled, and surfaces
          this role can&apos;t access are hidden.
        </Text>
        <Button
          size="sm"
          variant="light"
          color="yellow"
          onClick={() => clearRole.mutate()}
          loading={clearRole.isPending}
        >
          Return to My Role
        </Button>
      </Group>
    </Alert>
  );
}
